import { defineStore } from 'pinia';
import { ref } from 'vue';

export interface ThemeConfig {
  name: string;
  primary: string;
  hover: string;
  dark: string;
  tintBg: string;
  tintBorder: string;
  subtleBg: string;
  subtleBorder: string;
  activeBorder: string;
}

export const THEMES: Record<string, ThemeConfig> = {
  orange: {
    name: 'Gateflow Orange',
    primary: '#ea580c',
    hover: '#d94315',
    dark: '#c2410c',
    tintBg: '#fff5f0',
    tintBorder: '#fed8c9',
    subtleBg: '#fff6f2',
    subtleBorder: '#f5bfa8',
    activeBorder: '#c2410c',
  },
  blue: {
    name: 'Cobalt Blue',
    primary: '#2563eb',
    hover: '#1d4ed8',
    dark: '#1e40af',
    tintBg: '#eff6ff',
    tintBorder: '#bfdbfe',
    subtleBg: '#f0f7ff',
    subtleBorder: '#bfdbfe',
    activeBorder: '#1d4ed8',
  },
  green: {
    name: 'Emerald Green',
    primary: '#059669',
    hover: '#047857',
    dark: '#065f46',
    tintBg: '#ecfdf5',
    tintBorder: '#a7f3d0',
    subtleBg: '#f0fdf4',
    subtleBorder: '#bbf7d0',
    activeBorder: '#047857',
  },
  red: {
    name: 'Crimson Red',
    primary: '#dc2626',
    hover: '#b91c1c',
    dark: '#991b1b',
    tintBg: '#fef2f2',
    tintBorder: '#fecaca',
    subtleBg: '#fff5f5',
    subtleBorder: '#fed7d7',
    activeBorder: '#b91c1c',
  },
  gold: {
    name: 'Royal Gold',
    primary: '#d97706',
    hover: '#b45309',
    dark: '#92400e',
    tintBg: '#fffbeb',
    tintBorder: '#fde68a',
    subtleBg: '#fefce8',
    subtleBorder: '#fef08a',
    activeBorder: '#b45309',
  },
  purple: {
    name: 'Amethyst Purple',
    primary: '#7c3aed',
    hover: '#6d28d9',
    dark: '#5b21b6',
    tintBg: '#f5f3ff',
    tintBorder: '#ddd6fe',
    subtleBg: '#faf5ff',
    subtleBorder: '#e9d5ff',
    activeBorder: '#6d28d9',
  },
  cyan: {
    name: 'Cyber Cyan',
    primary: '#0891b2',
    hover: '#0e7490',
    dark: '#155e75',
    tintBg: '#ecfeff',
    tintBorder: '#a5f3fc',
    subtleBg: '#f0fdfa',
    subtleBorder: '#99f6e4',
    activeBorder: '#0e7490',
  },
  rose: {
    name: 'Rose Pink',
    primary: '#e11d48',
    hover: '#be123c',
    dark: '#9f1239',
    tintBg: '#fff1f2',
    tintBorder: '#fecdd3',
    subtleBg: '#fff5f7',
    subtleBorder: '#fed7e2',
    activeBorder: '#be123c',
  },
  lime: {
    name: 'Neon Lime',
    primary: '#65a30d',
    hover: '#4d7c0f',
    dark: '#3f6212',
    tintBg: '#f7fee7',
    tintBorder: '#d9f99d',
    subtleBg: '#fbfef0',
    subtleBorder: '#e2f9b8',
    activeBorder: '#4d7c0f',
  },
  indigo: {
    name: 'Electric Indigo',
    primary: '#4f46e5',
    hover: '#4338ca',
    dark: '#3730a3',
    tintBg: '#eef2ff',
    tintBorder: '#c7d2fe',
    subtleBg: '#f5f7ff',
    subtleBorder: '#d5ddff',
    activeBorder: '#4338ca',
  },
  slate: {
    name: 'Titanium Slate',
    primary: '#475569',
    hover: '#334155',
    dark: '#1e293b',
    tintBg: '#f8fafc',
    tintBorder: '#cbd5e1',
    subtleBg: '#f1f5f9',
    subtleBorder: '#e2e8f0',
    activeBorder: '#334155',
  },
  sunset: {
    name: 'Sunset Coral',
    primary: '#f43f5e',
    hover: '#e11d48',
    dark: '#be123c',
    tintBg: '#fff1f2',
    tintBorder: '#ffe4e6',
    subtleBg: '#fff5f5',
    subtleBorder: '#fed7d7',
    activeBorder: '#e11d48',
  },
};

export const useUiStore = defineStore('ui', () => {
  const isSidebarCollapsed = ref(
    typeof window !== 'undefined' ? localStorage.getItem('sidebar_collapsed') === 'true' : false
  );
  const isMobileMenuOpen = ref(false);

  // Theme & Styling configuration
  const activeThemeKey = ref<string>(
    typeof window !== 'undefined' ? localStorage.getItem('gateflow_theme') || 'orange' : 'orange'
  );
  const isDarkMode = ref<boolean>(
    typeof window !== 'undefined' ? localStorage.getItem('gateflow_dark_mode') === 'true' : false
  );
  const lineContrast = ref<'faint' | 'sharp'>(
    typeof window !== 'undefined'
      ? (localStorage.getItem('gateflow_line_contrast') as 'faint' | 'sharp') || 'faint'
      : 'faint'
  );

  function applyColorTheme(themeKey: string) {
    const theme = THEMES[themeKey] ?? THEMES['orange']!;
    activeThemeKey.value = themeKey;

    if (typeof window !== 'undefined') {
      localStorage.setItem('gateflow_theme', themeKey);
      const root = document.documentElement;
      root.style.setProperty('--theme-primary', theme.primary);
      root.style.setProperty('--theme-hover', theme.hover);
      root.style.setProperty('--theme-dark', theme.dark);
      root.style.setProperty('--theme-tint-bg', theme.tintBg);
      root.style.setProperty('--theme-tint-border', theme.tintBorder);
      root.style.setProperty('--theme-subtle-bg', theme.subtleBg);
      root.style.setProperty('--theme-subtle-border', theme.subtleBorder);
      root.style.setProperty('--theme-active-border', theme.activeBorder);
    }
  }

  function setDarkMode(val: boolean) {
    isDarkMode.value = val;
    if (typeof window !== 'undefined') {
      localStorage.setItem('gateflow_dark_mode', String(val));
      if (val) {
        document.body.classList.add('dark-mode');
        document.documentElement.classList.add('dark');
        document.documentElement.setAttribute('data-theme', 'dark');
      } else {
        document.body.classList.remove('dark-mode');
        document.documentElement.classList.remove('dark');
        document.documentElement.setAttribute('data-theme', 'light');
      }
    }
  }

  function toggleDarkMode() {
    setDarkMode(!isDarkMode.value);
  }

  function setLineContrast(val: 'faint' | 'sharp') {
    lineContrast.value = val;
    if (typeof window !== 'undefined') {
      localStorage.setItem('gateflow_line_contrast', val);
      if (val === 'sharp') {
        document.body.classList.add('sharp-lines');
      } else {
        document.body.classList.remove('sharp-lines');
      }
    }
  }

  function toggleLineContrast() {
    setLineContrast(lineContrast.value === 'sharp' ? 'faint' : 'sharp');
  }

  function initThemes() {
    if (typeof window !== 'undefined') {
      applyColorTheme(activeThemeKey.value);
      setDarkMode(isDarkMode.value);
      setLineContrast(lineContrast.value);
    }
  }

  function toggleSidebar() {
    isSidebarCollapsed.value = !isSidebarCollapsed.value;
    if (typeof window !== 'undefined') {
      localStorage.setItem('sidebar_collapsed', String(isSidebarCollapsed.value));
    }
  }

  function toggleMobileMenu() {
    isMobileMenuOpen.value = !isMobileMenuOpen.value;
  }

  function closeMobileMenu() {
    isMobileMenuOpen.value = false;
  }

  return {
    isSidebarCollapsed,
    isMobileMenuOpen,
    activeThemeKey,
    isDarkMode,
    lineContrast,
    applyColorTheme,
    setDarkMode,
    toggleDarkMode,
    setLineContrast,
    toggleLineContrast,
    initThemes,
    toggleSidebar,
    toggleMobileMenu,
    closeMobileMenu,
  };
});
